/* eslint-disable @typescript-eslint/no-unused-vars */
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

export interface HistoryColumn<T> {
  key: string;
  label: string;
  render?: (row: T) => React.ReactNode;
}

interface PartHistoryGroup<T> {
  partId: number;
  history: T[];
}

interface OrderHistoryData<TOrderEntry, TPartEntry> {
  orderHistory: TOrderEntry[];
  partHistory: PartHistoryGroup<TPartEntry>[];
}

interface OrderHistoryProps<TOrderEntry, TPartEntry> {
  title?: string;
  fetchHistory: () => Promise<OrderHistoryData<TOrderEntry, TPartEntry>>;
  orderColumns: HistoryColumn<TOrderEntry>[];
  partColumns: HistoryColumn<TPartEntry>[];
  variant?: "inline" | "dialog";
  open?: boolean;
  onClose?: () => void;
}

const getActionColor = (
  action: string
): "success" | "info" | "error" | "default" => {
  switch (action) {
    case "CREATE":
      return "success";
    case "UPDATE":
      return "info";
    case "DELETE":
      return "error";
    default:
      return "default";
  }
};

export const parseHistoryDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value !== "string") {
    const date = new Date(value as any);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const trimmed = value.trim();
  const dayMonthYearMatch = trimmed.match(/^\s*(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})\s*$/);

  if (dayMonthYearMatch) {
    const [, day, month, year, hours, minutes, seconds] = dayMonthYearMatch;
    const parsed = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      Number(seconds)
    );

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const defaultCellValue = (row: any, key: string) => {
  const value = row?.[key];
  if (value === null || value === undefined || value === "") return "-";
  if (key.toLowerCase().includes("at")) {
    const parsedDate = parseHistoryDate(value);
    if (!parsedDate) return value;
    return parsedDate.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }
  if (key === "action") {
    return <Chip label={value} size="small" color={getActionColor(value)} />;
  }
  return value;
};

export function OrderHistory<TOrderEntry, TPartEntry>({
  title = "Order History",
  fetchHistory,
  orderColumns,
  partColumns,
  variant = "inline",
  open = false,
  onClose,
}: OrderHistoryProps<TOrderEntry, TPartEntry>) {
  const [data, setData] = useState<OrderHistoryData<TOrderEntry, TPartEntry> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shouldFetch = variant === "inline" ? true : open;

  useEffect(() => {
    if (!shouldFetch) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchHistory();
      setData(result);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || "Failed to load history"
      );
    } finally {
      setLoading(false);
    }
  };

  const renderTable = <T,>(rows: T[], columns: HistoryColumn<T>[]) => (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
            {columns.map((col) => (
              <TableCell key={col.key} sx={{ fontWeight: 600 }}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length ? (
            rows.map((row, idx) => (
              <TableRow key={idx}>
                {columns.map((col) => (
                  <TableCell key={col.key}>
                    {col.render
                      ? col.render(row)
                      : defaultCellValue(row, col.key)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                sx={{ textAlign: "center", color: "text.secondary" }}>
                No history found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const content = (
    <Box>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Typography color="error" sx={{ py: 2, textAlign: "center" }}>
          {error}
        </Typography>
      )}

      {!loading && !error && data && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Status History
          </Typography>
          {renderTable(data.orderHistory, orderColumns)}

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Part History
          </Typography>
          {data.partHistory.length ? (
            data.partHistory.map((part) => (
              <Box key={part.partId} sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Part #{part.partId}
                </Typography>
                {renderTable(part.history, partColumns)}
              </Box>
            ))
          ) : (
            <Typography sx={{ color: "text.secondary" }}>
              No part history found
            </Typography>
          )}
        </>
      )}
    </Box>
  );

  if (variant === "dialog") {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
          }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HistoryIcon />
            {title}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{content}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return content;
}